import { format, parseISO } from 'date-fns'
import isEmpty from 'lodash/isEmpty'
import { formatIsoString, capitalize } from '../utilities'

export function maybeRedacted(value) {
  return value === 'redacted' ? value : null
}

export const normalizeHierarchicalLocations = locations => {
  // For each location item, merges the lowest hierarchical location reference
  // object with a name (meaning it's known) into the base.  If no name is
  // found on the expected objects, filter out the item.
  return locations.reduce((locations, location) => {
    if (!location) {
      return locations
    }

    if (location.country?.name) {
      location = { ...location, ...location.country }
    } else if (location.sub_region?.name) {
      location = { ...location, ...location.sub_region }
    } else if (location.region?.name) {
      location = { ...location, ...location.region }
    } else {
      return locations
    }

    return [...locations, location]
  }, [])
}

export const SCOPES = {
  confirmed: 'confirmed',
  suspected: 'suspected',
  possible: 'possible',
}

/** format nested history to a flattened array */
export function historyFormatting(history) {
  if (!Array.isArray(history)) {
    return history
  }

  const out = []

  const normalizeItem = item => ({
    id: item.id,
    name: item?.name?.toUpperCase(),
    merged: item.merged,
    attribution_scope: SCOPES.confirmed,
  })

  const flatten = history => {
    history.filter(x => x).forEach(item => {
      out.push(normalizeItem(item))
      if (item.history) {
        flatten(item.history)
      }
    })
    return out
  }

  return flatten(history).sort(sortOnName)
}

export function suspectedGroupsFormatting(suspectedGroups) {
  if (!Array.isArray(suspectedGroups)) {
    return suspectedGroups
  }

  return suspectedGroups.filter(x => x).map(item => ({
    id: item.id,
    name: item?.name?.toUpperCase(),
    applied: item.applied,
    attribution_scope: item.attribution_scope,
  })).sort(sortOnName)
}

const sortOnName = (a, b) => {
  if (a.name === b.name) {
    return 0
  }
  if (a.name.length !== b.name.length) {
    return a.name.length - b.name.length
  }
  return b.name > a.name ? -1 : 1
}

export function normalizeActor(data) {
  return {
    aliases: data.aliases,
    audience: data.audience,
    countryCode: data.locations?.source?.[0]?.country?.iso2,
    cve: data.cve || [],
    description: data.description,
    id: data.id,
    industries: data.industries || [],
    lastReference: data.last_activity_time,
    lastUpdated: data.lastUpdated,
    malware: data.malware || [],
    motivations: data.motivations || [],
    name: data.name,
    observed: data.observed,
    sourceLocations: normalizeHierarchicalLocations(data.locations?.source || []),
    targetLocations: data.locations?.target || [],
    tools: data.tools || [],
  }
}

export function normalizeMalware(data) {
  const isMalicious = data.is_malicious || data.inherently_malicious === 1
  return {
    actors: data.actors || [],
    aliases: data.aliases,
    audience: data.audience,
    capabilities: data.capabilities,
    cve: data.cve || [],
    counts: data.counts,
    description: data.description,
    fireeye_detection_names: data.detections,
    id: data.id,
    lastUpdated: data.last_activity_time,
    malware: data.malware || [],
    name: data.name,
    operating_systems: data.operating_systems,
    roles: data.roles || [],
    hasYara: data.has_yara,
    isMalicious
  }
}

export function normalizeVuln(data) {
  const {
    date_of_disclosure: disclosureDate,
    cisa_known_exploited: cisaKnownExploited = {},
    associated_actors: associatedActors = [],
    associated_malware: associatedMalware = [],
    risk_rating: risk,
    publish_date: publishedDate,
    associated_reports: associatedReports = [],
    ...rest
  } = data
  return {
    ...rest,
    associatedActors: sortedActors(associatedActors),
    associatedMalware: sortedMalware(associatedMalware),
    risk: Array.isArray(risk) ? capitalize(risk[0]) : capitalize(risk),
    disclosureDate,
    cisaKnownExploited: !isEmpty(cisaKnownExploited) ? cisaKnownExploited : null,
    publishedDate,
    cveReports: normalizeCveReports(associatedReports),
    associatedTools: Array.isArray(associatedMalware) ? associatedMalware.filter(malware => !malware.is_malicious) : [],
  }
}

export function normalizeCve(data) {
  return {
    ...data,
    associated_actors: sortedActors(data.associated_actors),
    associated_malware: sortedMalware(data.associated_malware),
    risk_rating: Array.isArray(data.risk_rating) ? capitalize(data.risk_rating[0]) : capitalize(data.risk_rating), // eslint-disable-line
  }
}

export function normalizeCampaign(data) {
  return {
    ...data,
    earliestReference: data.timeline && normalizeEarliestReference(data.timeline),
    lastReference: parseISO(data.last_activity_time.slice(0, -5)),
    sourceLocations: normalizeSourceLocations(data.actors),
    targetLocations: data.target_locations.countries,
    timeline: { id: data.id, data: data.timeline },
    reportedAssociations: normalizeReportedAssociations(data.aliases),
  }
}

const normalizeSourceLocations = (actors) => {
  if (!actors) return
  const normalized = actors.reduce((acc, actor) => {
    const { source_locations: sourceLocations } = actor
    if (!sourceLocations.length) return acc
    if (sourceLocations[0].region?.name.toLowerCase() === 'unknown') return acc
    const value = sourceLocations[0].country || sourceLocations[0].sub_region || sourceLocations[0].region
    acc.push(value)
    return acc
  }, [])
  // Remaps the potential duplicates and returns unique objects
  return [...new Map(normalized.map(item => [item.name, item])).values()]
}

const normalizeReportedAssociations = (aliases) => {
  if (!aliases) return
  const types = Object.keys(aliases).filter(key => Array.isArray(aliases[key])).sort()
  const associations = []
  types.forEach(key => {
    const typeArray = aliases[key]
    typeArray.forEach(item => {
      associations.push({ ...item, type: key })
    })
  })
  return associations
}

const normalizeEarliestReference = (timeline) => {
  if (!timeline?.length) return
  const firstObserved = timeline.filter(event => event.event_type === 'first_observed')
  if (!firstObserved?.length) return
  const date = parseISO(firstObserved[0].timestamp.slice(0, -5))
  return format(date, 'MMMM d, yyyy')
}

const sortedActors = (associatedActors) => {
  if (typeof associatedActors === 'string') {
    return associatedActors
  }
  if (!associatedActors.length > 0) {
    return []
  }
  return associatedActors.slice().sort((a, b) => a.name > b.name ? 1 : -1)
}

const sortedMalware = (associatedMalware) => {
  if (typeof associatedMalware === 'string') {
    return associatedMalware
  }
  if (!associatedMalware.length > 0) {
    return []
  }
  return associatedMalware.slice().sort((a, b) => a.name > b.name ? 1 : -1)
}

export function normalizeCveReports(reports) {
  return relevantReportsFormatting(reports || [], 'CVE')
}

export function normalizeCampaignReports(reports) {
  return relevantReportsFormatting(reports || [], 'Campaign')
}

export function normalizeActorHistory(data) {
  if (!data) { return data }

  const decorated = {}

  decorated.id = data.id
  decorated.name = data.name
  decorated.history = historyFormatting(data.history || [])
  decorated.suspectedGroups = suspectedGroupsFormatting(data.suspected_groups || [])
  decorated.meta = {
    hasGroupAssociations:
      !!decorated.history.length && !!decorated.suspectedGroups.length
  }

  return decorated
}

export function normalizeActorReports(data) {
  return relevantReportsFormatting(data?.reports, 'Actor')
}

export function normalizeMalwareReports(data) {
  return relevantReportsFormatting(data?.reports, 'Malware')
}

/** Map data we need from relevant reports to an array and then sort by date with overview/group profile floated to top */
export function relevantReportsFormatting(reports, requestType) {
  if (!reports?.length) return

  const formattedReports = maybeRedacted(reports) || reports
    .reduce((acc, item) => {
      if (!item.title) return acc
      acc.push({
        name: item.title,
        date: item.published_date || item.created,
        url: item.report_id && `/reports/${item.report_id}`,
        reportType: item.report_type || item.metadata?.report_type?.[0], // metadata.report_type will be removed by NAUT-2176
        license_info: item.license_info,
        audience: item.audience,
        id: item.report_id,
      })
      return acc
    }, [])

  return formattedReports
    ? formattedReports.sort((a, b) => {
      // Float certain reports to the top
      if (!isOverviewOrProfile(a, requestType) && isOverviewOrProfile(b, requestType)) {
        return 1
      } else if (isOverviewOrProfile(a, requestType) && !isOverviewOrProfile(b, requestType)) {
        return -1
      }
      // Sort the rest by date
      if (a.date && b.date) {
        const aDate = new Date(a.date).toISOString()
        const bDate = new Date(b.date).toISOString()
        if (aDate < bDate) { return 1 }
        if (aDate > bDate) { return -1 }
      }
      return 0
    })
    : []
}

const isOverviewOrProfile = (item, requestType) => {
  const { reportType } = item
  return reportType && (reportType.includes(`${requestType} Overview`) || reportType.includes(`${requestType} Profile`))
}

export const normalizeSelectedOrg = claims => {
  if (!claims) { return claims }
  const selectedOrg = claims.organizations.find(org => org.uuid === claims.org)
  return { uuid: claims.org, name: selectedOrg.name }
}

export const normalizeLinearLine = (timeline) => {
  const { data } = timeline
  if (data === undefined) return
  const filtered = data.filter(point => point.timestamp)
  const formatted = filtered.map((point, i) => {
    const { timestamp, event_type: eventType, name = '', description = '', ...rest } = point || {}

    if (!timestamp) {
      return null
    }

    return {
      ...rest,
      id: `${i}-${eventType}`,
      description,
      x: formatIsoString(timestamp),
      y: 0,
      name: name || 'Technique Observed',
      eventType: eventType || 'technique_observed',
    }
  }).filter(Boolean)
  formatted.sort((a, b) => new Date(a.x) - new Date(b.x))
  return [{ id: timeline.id, data: formatted }]
}
